/**
 * The frame: AppShell, Breadcrumb, Stepper.
 *
 * One shell, one audience. The nav is 264px of grouped destinations with live counts, because the
 * count IS the work — "KYC queue 12" is the reason an operator clicks, and a nav that hides it
 * makes them open every section to find where the backlog is.
 *
 * The rail collapses to icons for a narrow laptop. It never collapses to a hamburger: this console
 * is a desktop tool and hiding the map behind a tap costs more than the 264px it saves.
 */
import type { CSSProperties, ReactNode } from 'react';
import { Icon, Kicker } from './primitives';
import type { IconName } from './icons';

/**
 * Hoisted out of JSX deliberately: the icon generator scans for `name="…"`, and a conditional
 * literal inside `name={…}` both hides a glyph from it and reads worse than a named map.
 */
const RAIL_ICON = { collapsed: 'panel-left-open', expanded: 'panel-left-close' } satisfies Record<string, IconName>;
const THEME_ICON = { dark: 'sun', light: 'moon' } satisfies Record<'dark' | 'light', IconName>;

/* ────────────────────────────  AppShell  ──────────────────────────── */

export interface NavItem {
  key: string;
  label: string;
  icon: IconName;
  /** Outstanding work in that section. Absent when there is nothing to count. */
  count?: number;
}

export interface NavGroup {
  label: string;
  items: readonly NavItem[];
}

export interface ShellUser {
  email: string;
  scope: string;
}

export interface AppShellProps {
  nav: readonly NavGroup[];
  active?: string;
  onNavigate?: (key: string) => void;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  /** A non-production banner — 'staging', 'local'. Absent in production, deliberately. */
  environment?: string;
  user?: ShellUser;
  theme?: 'light' | 'dark';
  onThemeToggle?: () => void;
  onSearch?: () => void;
  title?: string;
  description?: string;
  actions?: ReactNode;
  breadcrumb?: ReactNode;
  /** The account menu — rendered by the shell furniture, not by this component. */
  account?: ReactNode;
  children?: ReactNode;
  style?: CSSProperties;
}

export function AppShell({
  nav,
  active,
  onNavigate,
  collapsed = false,
  onToggleCollapsed,
  environment,
  user,
  theme = 'light',
  onThemeToggle,
  onSearch,
  title,
  description,
  actions,
  breadcrumb,
  account,
  children,
  style,
}: AppShellProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `${collapsed ? 'var(--nav-admin-rail)' : 'var(--nav-admin)'} 1fr`,
        minHeight: '100%',
        background: 'var(--c-bg)',
        ...style,
      }}
    >
      <nav
        aria-label="Console sections"
        style={{
          background: 'var(--c-surface)',
          borderRight: '1px solid var(--c-border)',
          padding: 'var(--sp-md) var(--sp-sm)',
          display: 'grid',
          gap: 'var(--sp-xl)',
          alignContent: 'start',
        }}
      >
        {nav.map((group) => (
          <div key={group.label} style={{ display: 'grid', gap: 2 }}>
            {/* Collapsed, the group heading is dropped rather than truncated to noise. */}
            {!collapsed && <Kicker style={{ padding: '0 var(--sp-sm) var(--sp-xs)' }}>{group.label}</Kicker>}
            {group.items.map((it) => {
              const on = it.key === active;
              return (
                <button
                  key={it.key}
                  type="button"
                  onClick={() => onNavigate?.(it.key)}
                  aria-current={on ? 'page' : undefined}
                  title={collapsed ? it.label : undefined}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--sp-sm)',
                    height: 36,
                    width: '100%',
                    padding: '0 var(--sp-sm)',
                    border: 'none',
                    borderRadius: 'var(--r-tile)',
                    cursor: 'pointer',
                    background: on ? 'var(--c-saffron-50)' : 'transparent',
                    color: on ? 'var(--c-saffron-text)' : 'var(--c-ink2)',
                    fontFamily: 'var(--font-body)',
                    fontSize: 'var(--fs-admin-ui)',
                    fontWeight: on ? 'var(--fw-semibold)' : 'var(--fw-regular)',
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    textAlign: 'left',
                  }}
                >
                  <Icon name={it.icon} size={18} />
                  {!collapsed && <span style={{ flex: 1 }}>{it.label}</span>}
                  {!collapsed && it.count !== undefined && it.count > 0 && (
                    <span className="tabular" style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
                      {it.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      <div style={{ display: 'grid', gridTemplateRows: 'auto 1fr', minWidth: 0 }}>
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-md)',
            height: 52,
            padding: '0 var(--gutter-admin)',
            background: 'var(--c-surface)',
            borderBottom: '1px solid var(--c-border)',
          }}
        >
          {onToggleCollapsed && (
            <button
              type="button"
              onClick={onToggleCollapsed}
              aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
              aria-expanded={!collapsed}
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                color: 'var(--c-ink2)',
                display: 'grid',
                placeItems: 'center',
                width: 32,
                height: 32,
              }}
            >
              <Icon name={collapsed ? RAIL_ICON.collapsed : RAIL_ICON.expanded} size={18} />
            </button>
          )}

          {/* A real button, not decorative text — ⌘K works, and so does clicking it. */}
          <button
            type="button"
            onClick={onSearch}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-sm)',
              flex: 1,
              minWidth: 0,
              height: 32,
              padding: '0 var(--sp-sm)',
              border: 'none',
              background: 'none',
              borderRadius: 'var(--r-tile)',
              color: 'var(--c-ink3)',
              font: 'inherit',
              fontSize: 'var(--fs-admin-cell)',
              cursor: onSearch ? 'pointer' : 'default',
              textAlign: 'left',
            }}
          >
            <Icon name="search" size={16} />
            <span
              style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              Search bookings, pandits, payouts…
            </span>
            <kbd className="mono-num" style={{ fontSize: 11 }}>
              ⌘K
            </kbd>
          </button>

          {/* Absent in production. A permanent banner is a banner nobody reads. */}
          {environment && (
            <span
              className="mono-num"
              style={{
                padding: '2px var(--sp-sm)',
                borderRadius: 'var(--r-chip)',
                background: 'var(--c-warn-bg)',
                color: 'var(--c-warn-text)',
                fontSize: 'var(--fs-admin-colhead)',
              }}
            >
              {environment}
            </span>
          )}

          <button
            type="button"
            onClick={onThemeToggle}
            aria-label={theme === 'dark' ? 'Switch to the light theme' : 'Switch to the dark theme'}
            style={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              color: 'var(--c-ink2)',
              display: 'grid',
              placeItems: 'center',
              width: 32,
              height: 32,
            }}
          >
            <Icon name={THEME_ICON[theme]} size={18} />
          </button>

          {account ??
            (user && (
              <span style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
                {user.email} · <span style={{ color: 'var(--c-ink3)' }}>{user.scope}</span>
              </span>
            ))}
        </header>

        <main
          style={{
            padding: 'var(--sp-xxl) var(--gutter-admin)',
            display: 'grid',
            gap: 'var(--sp-lg)',
            alignContent: 'start',
            minWidth: 0,
          }}
        >
          {breadcrumb}
          {title && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--sp-lg)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h1
                  style={{
                    margin: 0,
                    fontFamily: 'var(--font-display)',
                    fontSize: 'var(--fs-admin-page-title)',
                    fontWeight: 'var(--fw-semibold)',
                    color: 'var(--c-ink)',
                  }}
                >
                  {title}
                </h1>
                {description && (
                  <p style={{ margin: '4px 0 0', fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink3)' }}>
                    {description}
                  </p>
                )}
              </div>
              <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'center' }}>{actions}</div>
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}

/* ────────────────────────────  Breadcrumb  ──────────────────────────── */

export interface Crumb {
  label: string;
  href?: string;
  onClick?: () => void;
}

/** Where you are, and one click back up. The last crumb is the page and is not a link. */
export function Breadcrumb({ items, style }: { items: readonly Crumb[]; style?: CSSProperties }) {
  return (
    <nav
      aria-label="Breadcrumb"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 'var(--sp-xs)',
        fontSize: 'var(--fs-admin-cell)',
        ...style,
      }}
    >
      {items.map((it, i) => {
        const last = i === items.length - 1;
        return (
          <span key={it.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-xs)' }}>
            {last ? (
              <span aria-current="page" style={{ color: 'var(--c-ink3)' }}>
                {it.label}
              </span>
            ) : it.href || it.onClick ? (
              <a
                href={it.href ?? '#'}
                onClick={it.onClick}
                style={{ color: 'var(--c-saffron-text)' }}
              >
                {it.label}
              </a>
            ) : (
              // An ancestor with nowhere to go is plain text, not a link that does nothing.
              <span style={{ color: 'var(--c-ink3)' }}>{it.label}</span>
            )}
            {!last && <span style={{ color: 'var(--c-ink3)' }}>›</span>}
          </span>
        );
      })}
    </nav>
  );
}

/* ────────────────────────────  Stepper  ──────────────────────────── */

export interface Step {
  label: string;
  note?: string;
}

export interface StepperProps {
  steps: readonly (Step | string)[];
  current?: number;
  orientation?: 'horizontal' | 'vertical';
  style?: CSSProperties;
}

/** Multi-stage flows: KYC review, a price-version publish, a payout run. */
export function Stepper({ steps, current = 0, orientation = 'horizontal', style }: StepperProps) {
  const vertical = orientation === 'vertical';
  return (
    <ol
      style={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'flex',
        flexDirection: vertical ? 'column' : 'row',
        gap: vertical ? 'var(--sp-lg)' : 'var(--sp-md)',
        alignItems: vertical ? 'stretch' : 'center',
        ...style,
      }}
    >
      {steps.map((s, i) => {
        const label = typeof s === 'string' ? s : s.label;
        const note = typeof s === 'string' ? undefined : s.note;
        const done = i < current;
        const now = i === current;
        return (
          <li
            key={label}
            aria-current={now ? 'step' : undefined}
            style={{
              display: 'flex',
              alignItems: vertical ? 'flex-start' : 'center',
              gap: 'var(--sp-sm)',
              flex: vertical ? undefined : 1,
            }}
          >
            <span
              style={{
                width: 24,
                height: 24,
                borderRadius: 'var(--r-pill)',
                display: 'grid',
                placeItems: 'center',
                flex: '0 0 auto',
                background: done ? 'var(--c-saffron)' : 'transparent',
                border: now ? '2px solid var(--c-saffron)' : done ? 'none' : '1px solid var(--c-border-strong)',
                color: done ? 'var(--c-ink-inverse)' : now ? 'var(--c-saffron)' : 'var(--c-ink3)',
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--fs-admin-colhead)',
                fontWeight: 'var(--fw-semibold)',
              }}
            >
              {done ? <Icon name="check" size={14} /> : i + 1}
            </span>
            <span style={{ display: 'grid', minWidth: 0 }}>
              <span
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--fs-admin-cell)',
                  fontWeight: now ? 'var(--fw-semibold)' : 'var(--fw-regular)',
                  color: now || done ? 'var(--c-ink)' : 'var(--c-ink3)',
                }}
              >
                {label}
              </span>
              {note && <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>{note}</span>}
            </span>
            {!vertical && i < steps.length - 1 && (
              <span style={{ flex: 1, height: 1, background: 'var(--c-border)' }} />
            )}
          </li>
        );
      })}
    </ol>
  );
}
